/**
 * New Project dialog — the single project-creation / promotion surface
 * (docs/plans/2026-07-12_PROJECT_ACCESS_ON_CREATE.md, §3).
 *
 * A glass wizard whose pages slide by DIRECTION (forward → in from the right,
 * back → from the left), so "+ add" from the list moves forward, not back:
 *   1. SCOPE  — Follow the parent folder (live inheritance, with a summary of
 *               who that is) / a team / a role / certain people, plus "Only me".
 *   2. PICKER — a full-page team/role/member picker (never a cropped popover),
 *               opened on the tab the user asked for. Has Back.
 *   3. LIST   — the real who-has-access list: You, the managers INHERITED from
 *               the parent (owners + whichever admins actually resolve — NOT a
 *               blanket claim), and any added principals. Back + Create.
 *
 * Modes: Follow-parent → Organization (unrestricted, live inheritance, D2);
 * added principals → Custom (restricted); neither → Private. Access spec + name
 * ride ONE POST /api/workspaces (D5). Promotion drops the name and restricts in
 * place, never moving the folder (D6). Glass per DESIGN_DOC §10.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { User, Shield, UsersRound, ChevronDown, ChevronRight, ChevronLeft, X, Folder, Search, Check } from 'lucide-react'
import { useTheme, getThemeClass, PDAppIcon } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { request } from '@papan/api-client'
import { AccessSettingsPopover } from '@papan/org-connections/ui'
import { avatarBg, initialFromEmail } from '@papan/share-links/ui'
import type { MemberInfo, TeamInfo, TagType } from './WhoHasAccessDialog.types.js'
import { ROLE_LABELS } from './WhoHasAccessDialog.types.js'
import { isMiniappFolder } from '@papan/types'
import { useProjects, projectDisplayName } from './home/data'
import { Boxes, LayoutGrid } from 'lucide-react'

type Tier = 'admin' | 'readwrite' | 'readcopy' | 'hidden'
type GrantTargetType = 'user' | 'role' | 'group'
type EntityKind = 'project' | 'miniapp'
/** miniapp: 'home' (what for?) → 'scope' (standalone) | 'parent' (for a project) → picker → list. */
type Page = 'home' | 'scope' | 'parent' | 'picker' | 'list'
type Dir = 'fwd' | 'back'

const TIER_LABELS: Record<Tier, string> = { admin: 'Manage & Edit', readwrite: 'Read & Write', readcopy: 'Read-only', hidden: 'No access' }
const TIER_OPTIONS: Tier[] = ['admin', 'readwrite', 'readcopy', 'hidden']
const ADDABLE_ROLES = ['admin', 'member', 'viewer', 'guest', 'noaccess']

interface GrantRow { targetType: GrantTargetType; targetId: string; permission: Tier; allowSharing: boolean; label: string }
interface InheritedRow { targetType: GrantTargetType; targetId: string; permission: string; label?: string }
interface AudienceResponse { restricted: boolean; adminPassthrough: boolean; principals: InheritedRow[]; containerId?: string | null; callerCanManage?: boolean }

const rowKey = (t: GrantTargetType, id: string) => `${t}:${id}`

export interface NewProjectDialogProps {
  open: boolean
  onClose: () => void
  isOrg: boolean
  tenantId: string
  tenantName?: string
  currentUserEmail: string
  /** What we're creating. 'miniapp' adds the "what's this for?" home step + the
   *  project/miniapp parent picker; otherwise identical machinery. */
  entityKind?: EntityKind
  variant?: 'create' | 'promote'
  initialName?: string
  /** Display name of the folder the project inherits from (create: its container;
   *  promote: the folder's parent). Shown on the "Follow the parent folder" row. */
  parentName?: string
  promoteFolderId?: string
  promoteNewName?: string
  onDone: (folderId: string) => void
}

export function NewProjectDialog(props: NewProjectDialogProps) {
  const { open, onClose, isOrg, tenantId, currentUserEmail, entityKind = 'project', variant = 'create', initialName, parentName, promoteFolderId, promoteNewName, onDone } = props
  const isMiniapp = entityKind === 'miniapp'
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const { t } = useTranslation('files')
  const me = currentUserEmail.toLowerCase()

  const [page, setPage] = useState<Page>('scope')
  const [dir, setDir] = useState<Dir>('fwd')
  const [pickerTab, setPickerTab] = useState<TagType>('member')
  const [pickerFrom, setPickerFrom] = useState<Page>('scope')
  // Child mini-app: the chosen parent it lives inside (null = standalone →
  // MiniApps container). `parentKind` filters the picker to projects XOR miniapps.
  const [parent, setParent] = useState<{ id: string; name: string } | null>(null)
  const [parentKind, setParentKind] = useState<'project' | 'miniapp'>('project')
  const [search, setSearch] = useState('')
  const [name, setName] = useState(initialName ?? '')
  // The restricted TOGGLE is the primary access state: ON = a boundary (only the
  // listed principals + managers); OFF = Follow the parent (inherit everyone).
  // Default ON; "Follow parent" turns it off.
  const [restricted, setRestricted] = useState(true)
  const [added, setAdded] = useState<GrantRow[]>([])
  const [followAudience, setFollowAudience] = useState<AudienceResponse | null>(null)
  // The same parent's audience AFTER the Restricted cut, computed by the server
  // (computeChildAudience(…, restricted=true)) — the list shown while Restricted
  // is on. Never derived here from the full audience (#787 §3: no client copy of
  // the cut).
  const [restrictedAudience, setRestrictedAudience] = useState<AudienceResponse | null>(null)
  // Private mode (restricted, no picks, no parent) lands in the creator's OWN space —
  // a different container than Custom's — so its Restricted list is fetched there (I12).
  const [privateAudience, setPrivateAudience] = useState<AudienceResponse | null>(null)
  const [allTeams, setAllTeams] = useState<TeamInfo[]>([])
  const [allMembers, setAllMembers] = useState<MemberInfo[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const nameRef = useRef<HTMLInputElement>(null)
  const [nameInvalid, setNameInvalid] = useState(false)
  // Projects + mini-apps to pick as a parent (mini-app "for a project" path).
  const projectsQuery = useProjects(open && isMiniapp)

  // Placement mode derives from the toggle: unrestricted → Follow parent (org);
  // restricted with no picks → Private; restricted with picks → Custom.
  const mode: 'private' | 'organization' | 'custom' = !restricted ? 'organization' : added.length > 0 ? 'custom' : 'private'

  const nav = (next: Page, d: Dir) => { setDir(d); setPage(next) }

  useEffect(() => {
    if (!open) return
    // miniapp starts at the "what's this for?" home step; project goes straight
    // to the access wizard (or the list on personal, which has no audience).
    setPage(isMiniapp ? 'home' : isOrg ? 'scope' : 'list')
    setDir('fwd'); setName(initialName ?? ''); setRestricted(true); setAdded([]); setParent(null); setError(null); setSearch(''); setNameInvalid(false)
  }, [open, initialName, isOrg, isMiniapp])

  useEffect(() => {
    if (!open || !isOrg) return
    Promise.all([
      request<{ teams: TeamInfo[] }>(`/api/orgs/${tenantId}/teams`).catch(() => ({ teams: [] })),
      // `{ members, invites }` — BOTH. This dialog's audience becomes folder
      // permissions, and a pending invitee is a valid grant target there (#112),
      // exactly as on the folder page and in "Share internally". Reading
      // `members` alone meant an admin could stage access for a new hire on an
      // EXISTING folder but not while creating the project they were hired for.
      // Declined invites excluded: re-inviting clears that state first.
      request<{ members: MemberInfo[]; invites?: MemberInfo[] }>(`/api/orgs/${tenantId}/members`)
        .catch(() => ({ members: [], invites: [] })),
    ]).then(([tr, mr]) => {
      setAllTeams(tr.teams ?? [])
      setAllMembers([
        ...(mr.members ?? []).map(m => ({ ...m, status: m.status ?? ('active' as const) })),
        ...(mr.invites ?? []).filter(i => i.status !== 'declined')
          .map(i => ({ ...i, email: i.email.toLowerCase(), status: 'invited' as const })),
      ])
    })
  }, [open, isOrg, tenantId])

  // The parent's FULL (unrestricted) audience — drives the "Follow parent"
  // summary AND, filtered per mode, the inherited managers shown on the list.
  const loadFollow = useCallback(async () => {
    if (!open || !isOrg) { setFollowAudience(null); setRestrictedAudience(null); setPrivateAudience(null); return }
    // A child mini-app inherits from its chosen parent; promotion from the
    // folder's real ancestors; everything else from the (Mini)Apps/Projects
    // container (same open grants, so the projects endpoint is representative).
    // `mode=custom` is that container simulated Restricted.
    const [url, restrictedUrl] = parent
      ? [`/api/files/${parent.id}/effective-audience?restricted=0`, `/api/files/${parent.id}/effective-audience?restricted=1`]
      : variant === 'promote' && promoteFolderId
        // Promotion restricts the folder IN PLACE (its own user/team grants stay) — not a
        // new child below it (I13).
        ? [`/api/files/${promoteFolderId}/effective-audience?restricted=0`, `/api/files/${promoteFolderId}/effective-audience?restricted=1&inPlace=1`]
        : [`/api/workspaces/audience?mode=organization`, `/api/workspaces/audience?mode=custom`]
    const standalone = !parent && !(variant === 'promote' && promoteFolderId)
    const [full, cut, priv] = await Promise.all([
      request<AudienceResponse>(url).catch(() => null),
      request<AudienceResponse>(restrictedUrl).catch(() => null),
      standalone ? request<AudienceResponse>(`/api/workspaces/audience?mode=private&restricted=1`).catch(() => null) : Promise.resolve(null),
    ])
    setFollowAudience(full)
    setPrivateAudience(priv)
    // A failed cut request leaves the Restricted list empty ("You" only) rather
    // than falling back to a client-side cut of the full list. Display only — the
    // server computes the real grants from `added` + `restricted` on create.
    setRestrictedAudience(cut)
  }, [open, isOrg, variant, promoteFolderId, parent])
  useEffect(() => { loadFollow() }, [loadFollow])

  const callerCanManage = followAudience?.callerCanManage ?? false
  // Principals that inherit MANAGE (admin/owner) from the parent. A creator who
  // doesn't manage the parent may keep them but NOT demote them (§ user rule):
  // their picker is locked to Manage & Edit.
  const inheritedManagers = useMemo(() => {
    const s = new Set<string>()
    for (const p of followAudience?.principals ?? []) if (p.permission === 'admin' || p.permission === 'owner') s.add(rowKey(p.targetType, p.targetId))
    return s
  }, [followAudience])
  const optionsFor = (t: GrantTargetType, id: string): Tier[] => (!callerCanManage && inheritedManagers.has(rowKey(t, id))) ? ['admin'] : TIER_OPTIONS

  const labelFor = (p: InheritedRow): string =>
    p.targetType === 'role' ? ROLE_LABELS[p.targetId] ?? p.targetId
    : p.targetType === 'group' ? (p.label ?? allTeams.find(t => t.id === p.targetId)?.name ?? 'Team')
    : (allMembers.find(m => m.email === p.targetId)?.name ?? p.targetId)

  // Inherited rows on the list: the full audience (Follow) or the server's
  // post-cut audience (Restricted — who keeps access through the boundary, i.e.
  // manages the parent), minus You and any row the user overrode.
  const inheritedRows: InheritedRow[] = useMemo(() => {
    if (!isOrg) return []
    return selectInheritedRows({
      restricted, full: followAudience?.principals ?? null,
      cut: restrictedListFor(mode, !parent && !(variant === 'promote' && promoteFolderId), privateAudience, restrictedAudience)?.principals ?? null,
      me, overridden: new Set(added.map(r => rowKey(r.targetType, r.targetId))),
    })
  }, [isOrg, followAudience, restrictedAudience, privateAudience, mode, parent, variant, promoteFolderId, restricted, added, me])

  // "Follow parent" summary — AccessBar-style FLOOR label: the weakest role that
  // can access, so "everyone viewer-and-up" reads as "Viewer+" (not an
  // enumeration). Owner/admin are implied managers; when no member-level role
  // reaches it, the teams/people who DO reach it are named (a No-access row
  // reaches nothing, so it is never named), and "Limited" only when nobody is —
  // the drive's locked-folder rule (#787 §3).
  const followSummary = useMemo(() => {
    if (!followAudience) return ''
    const floor = followRoleFloor(followAudience.principals)
    if (floor === 'viewer') return t('newProject.floorViewer', 'Viewer+')
    if (floor === 'member') return t('newProject.floorMember', 'Member+')
    const others = followNamedPrincipals(followAudience.principals, me).map(labelFor)
    if (others.length) return others.slice(0, 2).join(', ') + (others.length > 2 ? ` +${others.length - 2}` : '')
    return t('driveView.access.limited')
  }, [followAudience, allTeams, allMembers, me])

  const upsertRow = (row: GrantRow) => setAdded(prev => {
    const i = prev.findIndex(r => r.targetType === row.targetType && r.targetId === row.targetId)
    if (i === -1) return [...prev, row]; const next = prev.slice(); next[i] = row; return next
  })
  const removeRow = (t: GrantTargetType, id: string) => setAdded(prev => prev.filter(r => !(r.targetType === t && r.targetId === id)))
  const isSelected = (t: GrantTargetType, id: string) => added.some(r => r.targetType === t && r.targetId === id)
  const gtypeOf = (tab: TagType): GrantTargetType => tab === 'team' ? 'group' : tab === 'role' ? 'role' : 'user'
  const toggle = (tab: TagType, id: string, label: string) => {
    const gt = gtypeOf(tab)
    if (isSelected(gt, id)) removeRow(gt, id)
    else upsertRow({ targetType: gt, targetId: id, permission: 'readwrite', allowSharing: false, label })
  }
  const openPicker = (tab: TagType, from: Page) => {
    // Choosing a principal type from the SCOPE step implies a restricted project;
    // "+ add" from the list keeps whatever the toggle currently is.
    if (from === 'scope') setRestricted(true)
    setPickerTab(tab); setPickerFrom(from); setSearch(''); nav('picker', 'fwd')
  }

  const confirm = async () => {
    // Name is required for a create — if it's empty, highlight + focus the field
    // (it lives on THIS final page) rather than dead-disabling the button.
    if (variant === 'create' && !name.trim()) { setNameInvalid(true); nameRef.current?.focus(); return }
    setBusy(true); setError(null)
    const access = { restricted, grants: added.map(r => ({ targetType: r.targetType, targetId: r.targetId, permission: r.permission, allowSharing: r.allowSharing })) }
    const nm = name.trim() || 'Untitled'
    try {
      if (variant === 'promote' && promoteFolderId && promoteNewName) {
        await request(`/api/files/${promoteFolderId}`, { method: 'PUT', body: JSON.stringify({ name: promoteNewName, access }) })
        onDone(promoteFolderId)
      } else if (isMiniapp) {
        // Child → placed inside the chosen parent; standalone → MiniApps by mode.
        const body = parent ? { name: nm, parentFolderId: parent.id, access } : { name: nm, mode, access }
        const res = await request<{ folderId: string }>(`/api/workspaces/miniapp`, { method: 'POST', body: JSON.stringify(body) })
        onDone(res.folderId)
      } else {
        const res = await request<{ folderId: string }>(`/api/workspaces`, { method: 'POST', body: JSON.stringify({ name: nm, mode, access }) })
        onDone(res.folderId)
      }
      onClose()
    } catch (e: any) {
      const noManage = e?.code === 'promote_needs_manage'
      const taken = e?.code === 'name_taken' || /already exists/i.test(String(e?.message ?? e))
      setError(noManage ? t('newProject.errors.needManage', 'You need manage permission to make this a project')
        : taken ? t('newProject.errors.nameTaken', 'A project with this name already exists')
          : t('newProject.errors.failed', 'Could not create the project'))
    } finally { setBusy(false) }
  }

  if (!open) return null

  // Placement label baked into the Create button ("Create in Projects").
  const container = isMiniapp ? 'PDApps' : 'Projects'
  const placementLabel = variant === 'promote' ? ''
    : parent ? parent.name
    : mode === 'private' ? t('newProject.mySpace', 'My Space') : container
  const parentLabel = parentName ?? (variant === 'promote' ? t('newProject.theParent', 'the parent folder') : container)
  const noun = isMiniapp ? t('newProject.nounMiniapp', 'PDApp') : t('newProject.nounProject', 'project')

  // ── Page 1: scope ───────────────────────────────────────────────────
  const scopeRow = (ic: React.ReactNode, label: string, sub: string, onClick: () => void) => (
    <button onClick={onClick} className="w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-left ws-glass-well hover:ws-glass-chip transition-colors">
      <span className="themed-icon shrink-0">{ic}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm themed-text">{label}</span>
        <span className="block text-[11px] themed-text-muted truncate">{sub}</span>
      </span>
      <ChevronRight size={16} className="themed-text-muted shrink-0" />
    </button>
  )
  const scopePage = (
    <div className="px-5 py-4 space-y-3">
      <div className="text-xs themed-text-muted">{t('newProject.whoCanAccessX', 'Who can access this {{noun}}?', { noun })}</div>
      <div className="space-y-2">
        {scopeRow(<Folder size={15} className="themed-text-muted" />, t('newProject.followParent', 'Follow default {{parent}} permission', { parent: parentLabel }), followSummary || t('newProject.followSub', 'Same access as the parent folder'), () => { setRestricted(false); nav('list', 'fwd') })}
        {scopeRow(<UsersRound size={15} />, t('newProject.forTeams', 'For my teams'), t('newProject.forTeamsSub', 'Pick one or more teams'), () => openPicker('team', 'scope'))}
        {scopeRow(<Shield size={15} />, t('newProject.forRole', 'For a certain role'), t('newProject.forRoleSub', 'Members, viewers, admins…'), () => openPicker('role', 'scope'))}
        {scopeRow(<User size={15} />, t('newProject.forPeople', 'For certain people'), t('newProject.forPeopleSub', 'Pick specific members'), () => openPicker('member', 'scope'))}
      </div>
    </div>
  )

  // ── Page 2: picker ──────────────────────────────────────────────────
  const q = search.trim().toLowerCase()
  const pickerItems: Array<{ id: string; label: string }> =
    pickerTab === 'role' ? ADDABLE_ROLES.filter(r => !q || (ROLE_LABELS[r] ?? r).toLowerCase().includes(q)).map(r => ({ id: r, label: ROLE_LABELS[r] ?? r }))
    : pickerTab === 'team' ? allTeams.filter(tm => !q || tm.name.toLowerCase().includes(q)).map(tm => ({ id: tm.id, label: tm.name }))
    : allMembers.filter(m => m.email.toLowerCase() !== me).filter(m => !q || m.email.toLowerCase().includes(q) || (m.name?.toLowerCase().includes(q)))
        // Invitees first (they are who you are onboarding), and labelled: the
        // server sets `name = email` on an invite, so the naive form printed
        // "a@b.com (a@b.com)" and nothing distinguished a pending invitee from
        // a member. Same treatment as the folder page's picker.
        .sort((a, b) => Number(b.status === 'invited') - Number(a.status === 'invited'))
        .map(m => ({
          id: m.email,
          label: (m.name && m.name !== m.email ? `${m.name} (${m.email})` : m.email)
            + (m.status === 'invited' ? '  · Invited' : ''),
        }))
  const gtype = gtypeOf(pickerTab)
  const pickerPage = (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-3 pb-2">
        <div className="flex gap-1 mb-2">
          {(['role', 'team', 'member'] as TagType[]).map(tab => (
            <button key={tab} onClick={() => setPickerTab(tab)} className={`flex-1 py-1.5 text-xs rounded-lg transition-colors ${pickerTab === tab ? 'ws-glass-chip themed-text font-medium' : 'themed-text-muted hover:ws-glass-well'}`}>
              {t(`newProject.add.${tab}`, tab === 'role' ? 'Role' : tab === 'team' ? 'Team' : 'Member')}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 themed-text-muted" />
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder={t('newProject.pickerSearch', 'Search…')}
            className="w-full pl-7 pr-2 py-1.5 text-xs themed-input ws-glass-well rounded-lg border-0" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 pb-3 min-h-[200px] max-h-[46vh]">
        {pickerItems.length === 0 ? (
          <p className="px-3 py-6 text-xs themed-text-muted text-center">{t('newProject.pickerEmpty', 'Nothing to show')}</p>
        ) : pickerItems.map(it => {
          const sel = isSelected(gtype, it.id)
          return (
            <button key={it.id} onClick={() => toggle(pickerTab, it.id, pickerTab === 'member' ? (allMembers.find(m => m.email === it.id)?.name ?? it.id) : it.label)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-colors ${sel ? 'ws-glass-chip themed-text' : 'themed-text hover:ws-glass-well'}`}>
              <Check size={14} className={`shrink-0 ${sel ? 'text-blue-400 opacity-100' : 'opacity-0'}`} />
              <PrincipalAvatar type={gtype} id={it.id} />
              <span className="truncate flex-1">{it.label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )

  // ── Page 3: who-has-access list ─────────────────────────────────────
  const card = (key: React.Key, ic: React.ReactNode, title: string, sub: string | undefined, trailing: React.ReactNode) => (
    <div key={key} className="flex items-center gap-3 px-3.5 py-2.5 ws-glass-well rounded-xl">
      <span className="themed-icon shrink-0">{ic}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm themed-text truncate">{title}</span>
        {sub && <span className="block text-[11px] themed-text-muted truncate">{sub}</span>}
      </span>
      <span className="shrink-0">{trailing}</span>
    </div>
  )
  const listPage = (
    <div className="px-5 py-4 space-y-2.5">
      {variant === 'create' && (
        <div className="mb-1">
          <label className="block text-xs themed-text-muted mb-1">{t('newProject.nameLabel', 'Name')}</label>
          <input ref={nameRef} autoFocus value={name}
            onChange={e => { setName(e.target.value); if (nameInvalid) setNameInvalid(false) }}
            placeholder={t('newProject.namePlaceholder', 'Project name')}
            className={`w-full px-3 py-2 text-sm themed-input ws-glass-well rounded-lg border-0 ${nameInvalid ? 'ring-2 ring-red-400' : ''}`} />
          {nameInvalid && <div className="text-[11px] text-red-400 mt-1">{t('newProject.nameRequired', 'Give the project a name')}</div>}
        </div>
      )}
      {isOrg && (
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-base font-semibold themed-text">{t('newProject.whoHasAccess', 'Who has access')}</div>
            <div className="text-[11px] themed-text-muted">{restricted ? t('newProject.restrictedHint', 'Only the people below can access') : t('newProject.followHint', 'Inherits everyone from the parent folder')}</div>
          </div>
          <button onClick={() => setRestricted(r => !r)} role="switch" aria-checked={restricted} className="flex items-center gap-2 shrink-0 themed-text-muted">
            <span className="text-xs">{t('newProject.restricted', 'Restricted')}</span>
            <span className={`relative w-9 h-5 rounded-full transition-colors shrink-0 shadow-[inset_0_1px_3px_rgba(0,0,0,0.2)] ${restricted ? 'bg-violet-500' : 'themed-bg-primary'}`}>
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.35)] transition-all ${restricted ? 'left-[18px]' : 'left-0.5'}`} />
            </span>
          </button>
        </div>
      )}
      <div className="space-y-2">
        {card('you', <PrincipalAvatar type="user" id={me} />, t('newProject.you', 'You'), currentUserEmail, <span className="text-sm themed-text-muted px-1">{TIER_LABELS.admin}</span>)}
        {inheritedRows.map(r => card(`inh-${rowKey(r.targetType, r.targetId)}`, <PrincipalAvatar type={r.targetType} id={r.targetId} />, labelFor(r),
          `${TIER_LABELS[r.permission as Tier] ?? r.permission} · ${t('newProject.inherited', 'inherited')}`,
          <TierPicker themeClass={themeClass} value={r.permission as Tier} allowSharing={false} options={optionsFor(r.targetType, r.targetId)}
            inheritedLabel={`${TIER_LABELS[r.permission as Tier] ?? r.permission} (${t('newProject.inherited', 'inherited')})`}
            onInherit={() => removeRow(r.targetType, r.targetId)}
            onChange={(perm, share) => upsertRow({ targetType: r.targetType, targetId: r.targetId, permission: perm, allowSharing: share, label: labelFor(r) })} />,
        ))}
        {added.map(r => card(`add-${rowKey(r.targetType, r.targetId)}`, <PrincipalAvatar type={r.targetType} id={r.targetId} />, r.label,
          r.targetType === 'role' ? t('newProject.role', 'role') : r.targetType === 'group' ? t('newProject.team', 'team') : (r.label !== r.targetId ? r.targetId : undefined),
          <TierPicker themeClass={themeClass} value={r.permission} allowSharing={r.allowSharing} options={optionsFor(r.targetType, r.targetId)}
            onInherit={inheritedManagers.has(rowKey(r.targetType, r.targetId)) ? () => removeRow(r.targetType, r.targetId) : undefined}
            onChange={(perm, share) => upsertRow({ ...r, permission: perm, allowSharing: share })} onRemove={() => removeRow(r.targetType, r.targetId)} />,
        ))}
      </div>
      {isOrg && (
        <div className="flex gap-2 pt-1">
          {(['role', 'team', 'member'] as TagType[]).map(tab => (
            <button key={tab} onClick={() => openPicker(tab, 'list')} className="flex-1 py-2 text-xs themed-text-muted hover:themed-text rounded-lg ws-glass-well transition-colors">
              + {t(`newProject.add.${tab}`, tab === 'role' ? 'Role' : tab === 'team' ? 'Team' : 'Member')}
            </button>
          ))}
        </div>
      )}
      {isOrg && <div className="text-[11px] themed-text-muted pt-1">{t('newProject.ownerNote', 'The organization owner always has full access.')}</div>}
    </div>
  )

  // ── Mini-app home: what's this for? ─────────────────────────────────
  const homePage = (
    <div className="px-5 py-4 space-y-3">
      <div className="text-xs themed-text-muted">{t('newProject.miniappFor', "What's this PDApp for?")}</div>
      <div className="space-y-2">
        {scopeRow(<PDAppIcon size={15} className="text-fuchsia-500" />, t('newProject.standalone', 'A brand new PDApp'), t('newProject.standaloneSub', 'For general use'), () => { setParent(null); nav(isOrg ? 'scope' : 'list', 'fwd') })}
        {scopeRow(<LayoutGrid size={15} className="themed-text-muted" />, t('newProject.forProject', 'For a project'), t('newProject.forProjectSub', 'Opens in a project workspace'), () => { setParentKind('project'); setSearch(''); nav('parent', 'fwd') })}
        {scopeRow(<Boxes size={15} className="themed-text-muted" />, t('newProject.forMiniapp', 'For another PDApp'), t('newProject.forMiniappSub', 'Nested inside a PDApp'), () => { setParentKind('miniapp'); setSearch(''); nav('parent', 'fwd') })}
      </div>
    </div>
  )

  // ── Parent picker — filtered to ONE kind (project XOR MiniApp) ──────
  const parentItems = (projectsQuery.data ?? [])
    .filter(p => parentKind === 'miniapp' ? isMiniappFolder(p.name) : !isMiniappFolder(p.name))
    .filter(p => !q || projectDisplayName(p.name).toLowerCase().includes(q))
  const parentPage = (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-3 pb-2">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 themed-text-muted" />
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
            placeholder={parentKind === 'miniapp' ? t('newProject.parentSearchMiniapp', 'Search PDApps…') : t('newProject.parentSearchProject', 'Search projects…')}
            className="w-full pl-7 pr-2 py-1.5 text-xs themed-input ws-glass-well rounded-lg border-0" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 pb-3 min-h-[200px] max-h-[46vh]">
        {parentItems.length === 0 ? (
          <p className="px-3 py-6 text-xs themed-text-muted text-center">{parentKind === 'miniapp' ? t('newProject.parentEmptyMiniapp', 'No PDApps yet') : t('newProject.parentEmptyProject', 'No projects yet')}</p>
        ) : parentItems.map(p => (
          <button key={p.folderId} onClick={() => { setRestricted(false); setParent({ id: p.folderId, name: projectDisplayName(p.name) }); nav('list', 'fwd') }}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm themed-text hover:ws-glass-well transition-colors">
            {parentKind === 'miniapp' ? <PDAppIcon size={16} className="text-fuchsia-400 shrink-0" /> : <LayoutGrid size={16} className="text-indigo-400 shrink-0" />}
            <span className="truncate flex-1">{projectDisplayName(p.name)}</span>
          </button>
        ))}
      </div>
    </div>
  )

  const backTarget: Page | null =
    page === 'picker' ? pickerFrom
    : page === 'parent' ? 'home'
    : page === 'scope' ? (isMiniapp ? 'home' : null)
    : page === 'list' ? (isMiniapp ? (parent ? 'parent' : 'scope') : (isOrg ? 'scope' : null))
    : null
  const showBack = backTarget !== null
  const activePage = page === 'home' ? homePage : page === 'scope' ? scopePage : page === 'parent' ? parentPage : page === 'picker' ? pickerPage : listPage

  return createPortal(
    // Top-anchored (not centered) so the modal's top edge stays put as the list
    // grows/shrinks — content extends downward within a fixed height instead of
    // recentering and making the top jump.
    <div className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-[9vh] pb-4">
      <div className="absolute inset-0 bg-black/50 ws-anim-fade-in" onClick={busy ? undefined : onClose} />
      <div data-new-project-modal className={`${themeClass} ws-glass-modal ws-anim-modal-in relative w-full max-w-lg flex flex-col max-h-[82vh]`}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h3 className="text-base font-semibold themed-text flex items-center gap-2">
            {showBack && backTarget && (
              <button onClick={() => nav(backTarget, 'back')} className="themed-icon hover:themed-text -ml-1 p-1 rounded ws-glass-well" aria-label={t('common.back', 'Back')}>
                <ChevronLeft size={16} />
              </button>
            )}
            {variant === 'promote' ? t('newProject.titlePromote', 'Make this a project')
              : isMiniapp ? t('newProject.titleMiniapp', 'New PDApp')
              : t('newProject.title', 'New project')}
          </h3>
          <button onClick={onClose} disabled={busy} className="themed-icon hover:themed-text p-1 rounded ws-glass-well"><X size={16} /></button>
        </div>

        <div className="overflow-hidden flex-1 flex flex-col min-h-[320px]">
          <div key={page} className={`flex-1 overflow-y-auto ${dir === 'fwd' ? 'ws-anim-slide-right' : 'ws-anim-slide-left'}`}>{activePage}</div>
        </div>

        {error && <div className="px-5 text-xs text-red-400">{error}</div>}

        <div className="ws-glass-footer flex items-center justify-between gap-2 px-5 py-3">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 text-sm themed-text-muted hover:themed-text rounded-lg ws-glass-well transition-colors">{t('common.cancel', 'Cancel')}</button>
          <div className="flex items-center gap-2">
            {page === 'scope' && (
              <button onClick={() => { setRestricted(true); setAdded([]); nav('list', 'fwd') }} className="px-3 py-1.5 text-sm themed-text rounded-lg ws-glass-chip hover:opacity-90 transition-opacity">{t('newProject.onlyMe', 'Only me')}</button>
            )}
            {page === 'picker' && (
              added.length === 0
                ? <button onClick={() => nav('list', 'fwd')} className="px-3 py-1.5 text-sm themed-text rounded-lg ws-glass-chip hover:opacity-90 transition-opacity">{t('newProject.onlyMe', 'Only me')}</button>
                : <button onClick={() => nav('list', 'fwd')} className="px-4 py-1.5 text-sm themed-btn-primary rounded-lg flex items-center gap-1">{t('newProject.next', 'Next')} <ChevronRight size={14} /></button>
            )}
            {page === 'list' && (
              <button onClick={confirm} disabled={busy} className="px-4 py-1.5 text-sm themed-btn-primary rounded-lg disabled:opacity-50">
                {busy ? t('common.loading', 'Loading…') : variant === 'promote' ? t('newProject.make', 'Make project') : t('newProject.createIn', 'Create in {{where}}', { where: placementLabel })}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}

/** Principal avatar matching WhoHasAccessDialog's CardAvatar: members get a
 *  color-hashed initials disc, roles an amber shield, teams a blue people icon. */
function PrincipalAvatar({ type, id }: { type: GrantTargetType; id: string }) {
  const base = 'w-8 h-8 rounded-full flex items-center justify-center shrink-0 select-none'
  if (type === 'user') return <div className={`${base} ${avatarBg(id)} text-white text-[11px] font-semibold`}>{initialFromEmail(id)}</div>
  if (type === 'role') return <div className={`${base} bg-amber-500`}><Shield size={14} className="text-white" /></div>
  return <div className={`${base} bg-blue-500`}><UsersRound size={14} className="text-white" /></div>
}

/** Per-row tier picker (full internal option set incl. admin). Portaled. When
 *  `options` is narrowed (e.g. ['admin']) the row can be kept but not demoted. */
function TierPicker({ themeClass, value, allowSharing, onChange, onRemove, onInherit, inheritedLabel, options = TIER_OPTIONS }: {
  themeClass: string; value: Tier; allowSharing: boolean
  onChange: (perm: Tier, allowSharing: boolean) => void; onRemove?: () => void; onInherit?: () => void; inheritedLabel?: string; options?: Tier[]
}) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const dropRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return
      if (dropRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])
  useEffect(() => {
    if (!open || !wrapRef.current) return
    const r = wrapRef.current.getBoundingClientRect()
    const modal = wrapRef.current.closest('[data-new-project-modal]')?.getBoundingClientRect()
    const h = dropRef.current?.offsetHeight ?? 220
    let top = r.bottom + 4
    if (modal) top = Math.min(Math.max(top, modal.top + 8), Math.max(modal.top + 8, modal.bottom - h - 8))
    setPos({ top, left: Math.max(8, r.right - 168) })
  }, [open])
  return (
    <>
      <div ref={wrapRef}>
        <button onClick={() => setOpen(o => !o)} className="flex items-center gap-1 px-1 py-0.5 rounded hover:ws-glass-well">
          <span className="text-sm text-blue-400">{inheritedLabel ?? TIER_LABELS[value]}</span>
          <ChevronDown size={14} className="themed-text-muted" />
        </button>
      </div>
      {open && createPortal(
        <AccessSettingsPopover themeClass={themeClass} pos={pos} dropdownRef={dropRef} permissionOptions={options} permissionLabels={TIER_LABELS}
          selectedPermission={inheritedLabel ? null : value} onSelectPermission={opt => { onChange(opt as Tier, allowSharing); setOpen(false) }}
          inherit={onInherit ? { label: 'Inherit', selected: !!inheritedLabel, onClick: () => { setOpen(false); onInherit() } } : undefined}
          sharingAllowed={allowSharing} onSetSharing={allow => onChange(value, allow)}
          removeAction={onRemove ? { label: 'Remove', onClick: () => { setOpen(false); onRemove() } } : undefined} />,
        document.body,
      )}
    </>
  )
}

/**
 * The "Follow parent" preview's role floor: the weakest role that can reach the
 * new project, or null when no role grant reaches it. A role cut to No access
 * reaches nothing and never sets the floor; when only Manage-level roles reach it
 * the floor is 'limited' — like the drive's locked folders, never "Admin+", since
 * the Drive Admin role may be cut too (#787 §3).
 */
export function followRoleFloor(
  principals: ReadonlyArray<{ targetType: string; targetId: string; permission: string }>,
): 'viewer' | 'member' | 'limited' | null {
  const ranks = principals
    .filter(p => p.targetType === 'role' && p.targetId !== 'owner' && p.permission !== 'hidden' && p.permission !== 'none')
    .map(p => p.permission === 'readcopy' ? 1 : p.permission === 'readwrite' ? 2 : 3)
  if (ranks.length === 0) return null
  const floor = Math.min(...ranks)
  return floor === 1 ? 'viewer' : floor === 2 ? 'member' : 'limited'
}

/** The teams/people the "Follow parent" preview names when no member-level role
 *  reaches the new project: every non-role principal that REACHES it (a No-access
 *  row reaches nothing), minus the creator themself. */
export function followNamedPrincipals<P extends { targetType: string; targetId: string; permission: string }>(
  principals: ReadonlyArray<P>,
  me: string | null | undefined,
): P[] {
  return principals
    .filter(p => p.targetType !== 'role' && !(p.targetType === 'user' && p.targetId === me))
    .filter(p => p.permission !== 'hidden' && p.permission !== 'none')
}

/**
 * Which Restricted audience the list shows (I12): Private mode lands in the creator's
 * OWN space, so it shows that container's cut when one was fetched (standalone
 * projects); otherwise — Custom, or a project under a chosen parent — the parent /
 * org container's cut.
 */
export function restrictedListFor<T>(mode: 'private' | 'organization' | 'custom', standalone: boolean, privateAudience: T | null, restrictedAudience: T | null): T | null {
  // A standalone Private project lands in the creator's own space: show THAT list, and
  // if its request failed show nothing (never the org container's managers — the same
  // "no client-side fallback" rule as the Restricted list).
  if (mode === 'private' && standalone) return privateAudience
  return restrictedAudience
}

/**
 * The inherited rows the access list shows: the parent's FULL audience when the
 * new folder follows it, the server's POST-CUT audience when it is Restricted
 * (computeChildAudience(…, restricted=true) — who keeps access through the
 * boundary). Never a client-side cut of the full list (#787 §3). Under Restricted
 * a principal the cut leaves at No access doesn't reach the folder, so it's not a
 * row. The owner role, the creator and rows the user overrode are left out.
 */
export function selectInheritedRows<P extends { targetType: string; targetId: string; permission: string }>(args: {
  restricted: boolean
  full: ReadonlyArray<P> | null
  cut: ReadonlyArray<P> | null
  me: string
  overridden: ReadonlySet<string>
}): P[] {
  const audience = args.restricted ? args.cut : args.full
  if (!audience) return []
  return audience
    .filter(p => !(p.targetType === 'role' && p.targetId === 'owner'))
    .filter(p => !args.restricted || (p.permission !== 'hidden' && p.permission !== 'none'))
    .filter(p => !(p.targetType === 'user' && p.targetId === args.me))
    .filter(p => !args.overridden.has(rowKey(p.targetType as GrantTargetType, p.targetId)))
}
